"use client";

import { ListFilterIcon, SearchIcon, type LucideIcon } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  popoverRowClass,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ToolbarActiveDot } from "./toolbar-active-dot";

export type FilterSearchOption<Value extends string = string> = {
  value: Value;
  label: string;
  count?: number;
};

type FilterSearchPopoverProps<Value extends string> = {
  ariaLabel: string;
  /** Tells filters apart on a narrow screen, where only the icon shows. */
  icon?: LucideIcon;
  search: string;
  searchPlaceholder: string;
  onSearchChange: (value: string) => void;
  activeLabel: string;
  active: boolean;
  showSearch?: boolean;
  options?: Array<FilterSearchOption<Value>>;
  value?: Value;
  onValueChange?: (value: Value) => void;
  /** Settings below the options, such as how the list is arranged. */
  footer?: ReactNode;
};

export function FilterSearchOptionList({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-border/40">{children}</div>;
}

export function FilterSearchOptionButton({
  selected = false,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        "flex h-7 w-full items-center px-2 text-xs font-medium text-muted-foreground transition-colors",
        popoverRowClass(selected),
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function FilterSearchPopover<Value extends string>({
  ariaLabel,
  icon: Icon = ListFilterIcon,
  search,
  searchPlaceholder,
  onSearchChange,
  activeLabel,
  active,
  showSearch = true,
  options = [],
  value,
  onValueChange,
  footer,
}: FilterSearchPopoverProps<Value>) {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            className="relative h-7 gap-1.5 px-2"
            aria-label={ariaLabel}
            title={activeLabel}
          />
        }
      >
        <Icon className="size-3.5" />
        <span className="hidden sm:inline">{activeLabel}</span>
        {active ? <ToolbarActiveDot /> : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 overflow-hidden p-0">
        {showSearch ? (
          <div className="border-b border-border/50 p-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={searchPlaceholder}
                className="h-7 pl-8 text-sm"
              />
            </div>
          </div>
        ) : null}
        {options.length ? (
          <FilterSearchOptionList>
            {options.map((option) => {
              const selected = value === option.value;
              return (
                <FilterSearchOptionButton
                  key={option.value}
                  selected={selected}
                  onClick={() => onValueChange?.(option.value)}
                >
                  <span className="min-w-0 flex-1 truncate text-left">
                    {option.label}
                  </span>
                  {typeof option.count === "number" ? (
                    <span
                      className={cn(
                        "ml-2 rounded px-1 text-3xs tabular-nums",
                        selected
                          ? "bg-foreground/10 text-foreground"
                          : "bg-foreground/5 text-muted-foreground",
                      )}
                    >
                      {option.count}
                    </span>
                  ) : null}
                </FilterSearchOptionButton>
              );
            })}
          </FilterSearchOptionList>
        ) : null}
        {footer ? <div className="border-t border-border/50">{footer}</div> : null}
      </PopoverContent>
    </Popover>
  );
}
